import React, { useEffect, useRef } from "react";
import { Routes, Route, Navigate, BrowserRouter, useLocation } from "react-router-dom";
import "./toastStyles.css";

import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
// Import Datadog logging
import { initializeDatadogLogs, setDatadogUserContext } from "./utils/datadogLogger";
// Import PWA components
import InstallPrompt from "./components/InstallPrompt/InstallPrompt";
import { useServiceWorker } from "./hooks/useServiceWorker";
import useEnterAnswerShortcut from "./hooks/useEnterAnswerShortcut";
import LessonCom from "./components/lesson_Dashboard/LessonCom";
import CreateAccount from "./pages/auth/CreateAccount";
import CreateAccountForm from "./pages/CreateAccountForm";
import VerificationPage from "./pages/VerificationPage";
import LessonDashboard from "./components/lesson_Dashboard/LessonDashboard";
import TraceApp from "./components/Foundation/TraceCom";
import FoundationLessonCom from "./components/Foundation/FoundationLessonCom";
import SubscriptionPage from "./pages/payment";
import AfterLogin from "./pages/auth/AfterLogin";
import YearsDashboard from "./components/Years/YearsDashboard";
import SettingsDashboard from "./components/Profile/SettingsDashboard.js";
import ProfileEdit from "./components/Profile/ProfileEdit.js";
import BillingDetailsPage from "./components/Profile/BillingDetailsPage.jsx";
import About from "./assets/utils/About.js";
import FAQ from "./assets/utils/FAQ.js";
import ContactUs from "./components/Profile/ContactUs.js";
import YearSelection from "./components/Profile/YearSelection.js";
import AIHandler from "./components/HandleAIQuestions/AIHandler.js";
import Entry from "./components/Entry";
import { Sounds } from "./assets/asset.js";
import { Provider, useSelector } from "react-redux";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import store from "./store/store.js";
import LoginForm from "./pages/auth/LoginPage";
import Report from "./components/Leaderboard/ReportSection.jsx";
import Badges from "./components/Leaderboard/Badges.jsx";
import LogoutModal from "./components/LogoutModal.jsx";
import SessionTimeoutManager from "./components/SessionTimeoutManager.jsx";
import LeaderShipBoard from "./components/Leaderboard/LeadershipBoard.jsx";
import ForgotPasswordPage from "./pages/auth/ForgotPasswordPage";
import ResetPasswordPage from "./pages/auth/ResetPasswordPage";
import StudentOtpRequestPage from "./pages/auth/StudentOtpRequestPage";
import TutorInfoPage from "./pages/TutorInfoPage";
import TutorInfoDetailPage from "./pages/TutorInfoDetailPage";
import DeviceDetector from "./hooks/DeviceDetector.jsx";
import DemoDashboard from "./components/Years/DemoDashboard.js";
import AiParentsReport from "./components/Leaderboard/AiParentsReport.jsx";
import NotFoundPage from "./components/ui/NotFoundPage.jsx";
import ClassBookingDashboard from "./components/ClassBooking/Dashboard";
import BookTutor from "./components/ClassBooking/BookTutor";
import TutorDetails from "./components/ClassBooking/TutorDetails";
import BookingPage from "./components/ClassBooking/BookingPage";
import BookingConfirmed from "./components/ClassBooking/BookingConfirmed";
import MyBookings from "./components/ClassBooking/MyBookings";
import TeacherLogin from "./components/Teacher/TeacherLogin";
import TeacherOtpLogin from "./components/Teacher/TeacherOtpLogin";
import TeacherDashboard from "./components/Teacher/TeacherDashboard";
import TeacherAvailability from "./components/Teacher/TeacherAvailability";
import TeacherProfile from "./components/Teacher/TeacherProfile";
import TeacherUpdateProfile from "./components/Teacher/TeacherUpdateProfile";
import TeacherSignup from "./components/Teacher/TeacherSignup";
import TeacherOtpVerification from "./components/Teacher/TeacherOtpVerification";
import TeacherForgotPassword from "./components/Teacher/TeacherForgotPassword";
import TeacherResetPassword from "./components/Teacher/TeacherResetPassword";
import TeacherList from "./components/Teacher/TeacherList";
import TutorDetail from "./components/Teacher/TutorDetail";
import AdminCreateTeacherAddress from "./components/Teacher/AdminCreateTeacherAddress";
import TeacherLogoutModal from "./components/Teacher/TeacherLogoutModal";
import TeacherSessionTimeoutManager from "./components/Teacher/TeacherSessionTimeoutManager";
import MathDingoChatWidget from "./components/ChatWidget/MathDingoChatWidget";
import {
  TeacherPublicRoute,
  TeacherRouteGuard,
} from "./components/Teacher/TeacherRouteGuard";
import { SubscriptionProvider, useSubscription } from "./context/SubscriptionContext";
import { getSubscriptionPlanKey } from "./utils/subscriptionPlan";
import {
  AuthGuard,
  SubscriptionRouteGuard,
} from "./components/subscription/SubscriptionRouteGuard";
import {
  getAuthenticatedRedirectPath,
  isProfileIncomplete,
} from "./features/auth/profileCompletion";
import ParentLoginPage from "./pages/parent/ParentLoginPage";
import ParentSignupPage from "./pages/parent/ParentSignupPage";
import ParentVerificationPage from "./pages/parent/ParentVerificationPage";
import ParentForgotPasswordPage from "./pages/parent/ParentForgotPasswordPage";
import ParentAboutPage from "./pages/parent/ParentAboutPage";
import MobileSSOLogin from "./pages/MobileSSOLogin";
import {
  PARENT_DEFAULT_REDIRECT_PATH,
  isParentSessionMode,
  readParentPostAuthRedirect,
} from "./features/parentFlow/parentFlow";
import { GoogleOAuthProvider } from "@react-oauth/google";

const queryClient = new QueryClient();
const GOOGLE_TAG_HOSTNAME = "b2c.mathdingo.com";

const isTeacherFlowPath = (pathname = "", search = "") =>
  pathname.startsWith("/teacher") ||
  (pathname === "/reset-password" && !new URLSearchParams(search).get("id")) ||
  pathname.startsWith("/admin/teacher-address");

const ResetPasswordRoute = () => {
  const location = useLocation();
  const resetId = String(
    new URLSearchParams(location.search).get("id") || ""
  ).trim();

  if (resetId) {
    return <ResetPasswordPage />;
  }

  return <TeacherResetPassword />;
};

const ExternalRedirectPage = ({ url }) => {
  useEffect(() => {
    window.location.replace(url);
  }, [url]);

  return (
    <div className="min-h-screen flex items-center justify-center px-4 text-center">
      <p className="text-sm font-semibold text-[#0B2B66]">
        Redirecting...
        {" "}
        <a href={url} className="underline">
          Open link
        </a>
      </p>
    </div>
  );
};

const PublicRoute = ({ element }) => {
  const { user, token } = useSelector((state) => state.user);
  if (!token) return element;

  if (isParentSessionMode()) {
    return (
      <Navigate
        to={readParentPostAuthRedirect(PARENT_DEFAULT_REDIRECT_PATH)}
        replace
      />
    );
  }

  return <Navigate to={getAuthenticatedRedirectPath(user)} replace />;
};

const ParentPublicRoute = ({ element }) => {
  const { user, token } = useSelector((state) => state.user);
  if (!token) return element;

  if (isProfileIncomplete(user)) {
    return <Navigate to={getAuthenticatedRedirectPath(user)} replace />;
  }

  return (
    <Navigate
      to={readParentPostAuthRedirect(PARENT_DEFAULT_REDIRECT_PATH)}
      replace
    />
  );
};

const IndexRoute = () => {
  const { user, token } = useSelector((state) => state.user);
  if (!token) return <Entry />;

  if (isParentSessionMode()) {
    return (
      <Navigate
        to={readParentPostAuthRedirect(PARENT_DEFAULT_REDIRECT_PATH)}
        replace
      />
    );
  }

  return <Navigate to={getAuthenticatedRedirectPath(user)} replace />;
};

function App() {
  const location = useLocation();
  const audioRef = useRef(new Audio(Sounds.backgroundMusic));
  const hasTrackedInitialGooglePageView = useRef(false);
  const { showLogoutModal, user } = useSelector((state) => state.user);
  const { subscription, hasAccess } = useSubscription();
  const showTeacherLogoutModal = useSelector(
    (state) => state.teacher?.showLogoutModal
  );
  const { isMuted } = useSelector((state) => state.mute);
  const isTeacherFlow = isTeacherFlowPath(location.pathname, location.search);
  const isTutorInfoFlow =
    location.pathname === "/tutorinfo" ||
    location.pathname.startsWith("/tutorinfo/");
  const activePlanKey = getSubscriptionPlanKey(subscription, user);
  const canUseMdChatbot =
    hasAccess && (activePlanKey === "pro_monthly" || activePlanKey === "pro_yearly");

  // Initialize service worker for PWA
  useServiceWorker();
  useEnterAnswerShortcut();

  // Initialize Datadog on app load
  useEffect(() => {
    initializeDatadogLogs();
  }, []);

  // Set user context when user logs in
  useEffect(() => {
    if (user) {
      setDatadogUserContext(user);
    }
  }, [user]);

  useEffect(() => {
    if (window.location.hostname !== GOOGLE_TAG_HOSTNAME) return;
    if (!window.dataLayer) return;

    if (!hasTrackedInitialGooglePageView.current) {
      hasTrackedInitialGooglePageView.current = true;
      return;
    }

    window.dataLayer.push({
      event: "page_view",
      page_path: location.pathname + location.search,
    });
  }, [location.pathname, location.search]);

  useEffect(() => {
    const audio = audioRef.current;
    audio.loop = true;

    if (!isMuted) {
      audio.play().catch((error) => {
        console.log("Audio playback failed:", error);
      });
    } else {
      audio.pause();
    }

    return () => {
      audio.pause();
      audio.currentTime = 0;
    };
  }, [isMuted]);

  useEffect(() => {
    const backgroundColor = isTutorInfoFlow ? "#ffffff" : "#d7f6f7";
    const appRoot = document.getElementById("root");
    const backgroundTargets = [
      document.documentElement,
      document.body,
      appRoot,
    ].filter(Boolean);

    backgroundTargets.forEach((target) => {
      target.style.backgroundColor = backgroundColor;
    });
  }, [isTutorInfoFlow]);

  return (
    <>
      <div
        className={`responsive-orientation-shell min-h-screen overflow-x-hidden ${
          isTutorInfoFlow ? "bg-white" : "bg-[#d7f6f7]"
        }`}
      >
        <DeviceDetector />
        <MathDingoChatWidget canUseChatbot={canUseMdChatbot} />
        {!isTeacherFlow && <SessionTimeoutManager />}
        {isTeacherFlow && <TeacherSessionTimeoutManager />}
        {!isTeacherFlow && showLogoutModal && <LogoutModal />}
        {showTeacherLogoutModal && isTeacherFlow && <TeacherLogoutModal />}
        <Routes>
        <Route index element={<IndexRoute />} />
        <Route path="/tutorinfo" element={<TutorInfoPage />} />
        <Route path="/tutorinfo/:tutorId" element={<TutorInfoDetailPage />} />
        <Route
          path="/createaccount"
          element={<PublicRoute element={<CreateAccount />} />}
        />
        <Route
          path="/createaccountform"
          element={<PublicRoute element={<CreateAccountForm />} />}
        />
        <Route
          path="/verification"
          element={<PublicRoute element={<VerificationPage />} />}
        />
        <Route
          path="/createaccount-otp"
          element={<PublicRoute element={<StudentOtpRequestPage mode="signup" />} />}
        />
        <Route
          path="/login"
          element={<PublicRoute element={<LoginForm />} />}
        />
        <Route
          path="/login-otp"
          element={<PublicRoute element={<StudentOtpRequestPage mode="login" />} />}
        />
        <Route
          path="/parent/login"
          element={<ParentPublicRoute element={<ParentLoginPage />} />}
        />
        <Route
          path="/parent/signup"
          element={<ParentPublicRoute element={<ParentSignupPage />} />}
        />
        <Route
          path="/parent/verification"
          element={<ParentPublicRoute element={<ParentVerificationPage />} />}
        />
        <Route
          path="/parent/forgot-password"
          element={<ParentPublicRoute element={<ParentForgotPasswordPage />} />}
        />
        <Route path="/mobile-sso" element={<MobileSSOLogin />} />
        <Route
          path="/forgot-password"
          element={<PublicRoute element={<ForgotPasswordPage />} />}
        />
        <Route
          path="/teacherlogin"
          element={<TeacherPublicRoute element={<TeacherLogin />} />}
        />
        <Route
          path="/teacherlogin-otp"
          element={<TeacherPublicRoute element={<TeacherOtpLogin />} />}
        />
        <Route
          path="/teachersignup"
          element={<TeacherPublicRoute element={<TeacherSignup />} />}
        />
        <Route
          path="/teacherverify-otp"
          element={<TeacherPublicRoute element={<TeacherOtpVerification />} />}
        />
        <Route
          path="/teacherforgot-password"
          element={<TeacherPublicRoute element={<TeacherForgotPassword />} />}
        />
        <Route path="/reset-password" element={<ResetPasswordRoute />} />
        <Route path="/teachers" element={<TeacherList />} />
        <Route path="/tutors/:tutorId" element={<TutorDetail />} />
        <Route path="/admin/teacher-address" element={<AdminCreateTeacherAddress />} />
        <Route
          path="/teacherdashboard"
          element={<TeacherRouteGuard element={<TeacherDashboard />} />}
        />
        <Route
          path="/teacheravailability"
          element={<TeacherRouteGuard element={<TeacherAvailability />} />}
        />
        <Route
          path="/teacherprofile"
          element={<TeacherRouteGuard element={<TeacherProfile />} />}
        />
        <Route
          path="/teacherprofile/update"
          element={<TeacherRouteGuard element={<TeacherUpdateProfile />} />}
        />
        <Route path="/contact" element={<ContactUs />} />

        {/* Protected Routes */}
        <Route
          path="/afterlogin"
          element={<AuthGuard element={<AfterLogin />} />}
        />
        <Route
          path="/yearsdashboard"
          element={<AuthGuard element={<YearsDashboard />} />}
        />
        <Route
          path="/lessonCom"
          element={<SubscriptionRouteGuard element={<LessonCom />} />}
        />
        <Route
          path="/lessonDashboard"
          element={
            <SubscriptionRouteGuard
              element={<LessonDashboard />}
              restrictBasicToCurrentYear
            />
          }
        />
        <Route
          path="/trace"
          element={<SubscriptionRouteGuard element={<TraceApp />} />}
        />
        <Route
          path="/foundationLessonCom"
          element={
            <SubscriptionRouteGuard
              element={<FoundationLessonCom />}
              restrictBasicToCurrentYear
            />
          }
        />
        <Route
          path="/subscription"
          element={<AuthGuard element={<SubscriptionPage />} />}
        />
        <Route
          path="/subscription-terms"
          element={<ExternalRedirectPage url="https://mathdingo.com/terms_of_use.html" />}
        />
        <Route
          path="/settings-terms-policy"
          element={<ExternalRedirectPage url="https://mathdingo.com/terms_and_conditions.html" />}
        />
        <Route
          path="/settings"
          element={
            <SubscriptionRouteGuard
              element={<SettingsDashboard />}
              allowTrialOrActive
            />
          }
        />
        <Route
          path="/billing-details"
          element={<AuthGuard element={<BillingDetailsPage />} />}
        />
        <Route
          path="/editProffile"
          element={
            <SubscriptionRouteGuard
              element={<ProfileEdit />}
              allowTrialOrActive
            />
          }
        />
        <Route
          path="/about"
          element={<ParentAboutPage />}
        />
        <Route
          path="/settings/about"
          element={<SubscriptionRouteGuard element={<About />} allowTrialOrActive />}
        />
        <Route
          path="/faq"
          element={<SubscriptionRouteGuard element={<FAQ />} />}
        />

        <Route
          path="/report"
          element={
            <SubscriptionRouteGuard
              element={<Navigate to="/report/details" replace />}
              allowTrialOrActive
            />
          }
        />
        <Route
          path="/selectYear"
          element={<SubscriptionRouteGuard element={<YearSelection />} />}
        />
        <Route
          path="/AIHandler/:yearId"
          element={
            <SubscriptionRouteGuard
              element={<AIHandler />}
              requiredPlanType="pro"
            />
          }
        />
        <Route
          path="/report/details"
          element={<SubscriptionRouteGuard element={<Report />} allowTrialOrActive />}
        />
        <Route
          path="report/ai-report"
          element={
            <SubscriptionRouteGuard
              element={<AiParentsReport />}
              allowTrialOrActive
            />
          }
        />
        <Route
          path="/report/badges"
          element={<SubscriptionRouteGuard element={<Badges />} allowTrialOrActive />}
        />
        <Route
          path="/leaderboard"
          element={
            <SubscriptionRouteGuard
              element={<LeaderShipBoard />}
              allowTrialOrActive
            />
          }
        />
        <Route
          path="/demoDashboard"
          element={
            <SubscriptionRouteGuard
              element={<DemoDashboard />}
              restrictBasicToCurrentYear
            />
          }
        />
        <Route
          path="/classBooking/dashboard"
          element={<AuthGuard element={<ClassBookingDashboard />} />}
        />
        <Route
          path="/parent/classBooking/dashboard"
          element={<AuthGuard element={<ClassBookingDashboard />} />}
        />
        <Route
          path="/classBooking/bookTutor"
          element={<AuthGuard element={<BookTutor />} />}
        />
        <Route
          path="/parent/classBooking/bookTutor"
          element={<AuthGuard element={<BookTutor />} />}
        />
        <Route
          path="/classBooking/tutorDetails/:tutorId"
          element={<AuthGuard element={<TutorDetails />} />}
        />
        <Route
          path="/parent/classBooking/tutorDetails/:tutorId"
          element={<AuthGuard element={<TutorDetails />} />}
        />
        <Route
          path="/classBooking/booking"
          element={<AuthGuard element={<BookingPage />} />}
        />
        <Route
          path="/parent/classBooking/booking"
          element={<AuthGuard element={<BookingPage />} />}
        />
        <Route
          path="/classBooking/bookingConfirmed"
          element={<AuthGuard element={<BookingConfirmed />} />}
        />
        <Route
          path="/parent/classBooking/bookingConfirmed"
          element={<AuthGuard element={<BookingConfirmed />} />}
        />
        <Route
          path="/classBooking/myBookings"
          element={<AuthGuard element={<MyBookings />} />}
        />
        <Route
          path="/parent/classBooking/myBookings"
          element={<AuthGuard element={<MyBookings />} />}
        />
        <Route path="*" element={<NotFoundPage />} />
      </Routes>
    </div>
      <InstallPrompt />
      <div className="mobile-portrait-rotate-gate" role="status" aria-live="polite">
        <div className="mobile-portrait-rotate-card">
          <h1>Rotate your device</h1>
          <p>Please turn your phone sideways to continue.</p>
        </div>
      </div>
    </>
  );
}

const AppWrapper = () => {
  return (
    <Provider store={store}>
      <QueryClientProvider client={queryClient}>
        <GoogleOAuthProvider clientId={process.env.REACT_APP_GOOGLE_CLIENT_ID || ""}>
        <BrowserRouter>
          <SubscriptionProvider>
            <App />
          </SubscriptionProvider>
               <ToastContainer
            toastClassName="global-toast-bigger font-mali"
            bodyClassName="global-toast-body"
            progressClassName="global-toast-progress"
            position="top-right"
            autoClose={5000}
            hideProgressBar={false}
            newestOnTop
            closeOnClick
            rtl={false}
            pauseOnFocusLoss
            draggable
            pauseOnHover
            theme="light"
          />
        </BrowserRouter>
        </GoogleOAuthProvider>
      </QueryClientProvider>
    </Provider>
  );
};

export default AppWrapper;
